import React, { useState, useRef, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";

const ContextMenu = ({
  trigger,
  options,
  title,
  position = "bottom-left",
  onOptionClick,
  isOpen: controlledIsOpen,
  onOpenChange,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Use controlled state if provided, otherwise use internal state
  const isOpen =
    controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = useCallback(
    (value) => {
      if (controlledIsOpen !== undefined) {
        // If controlled, call the parent's callback
        if (onOpenChange) {
          onOpenChange(value);
        }
      } else {
        // If not controlled, use internal state
        setInternalIsOpen(value);
      }
    },
    [controlledIsOpen, onOpenChange]
  );

  const toggleMenu = useCallback(() => setIsOpen(!isOpen), [isOpen, setIsOpen]);

  const handleOptionClick = (option) => {
    if (onOptionClick) {
      onOptionClick(option);
    }
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        triggerRef.current &&
        menuRef.current &&
        !triggerRef.current.contains(event.target) &&
        !menuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  const getPositionClasses = () => {
    switch (position) {
      case "bottom-right":
        return "top-full right-0 mt-2";
      case "bottom-left":
        return "top-full left-0 mt-2";
      case "top-right":
        return "bottom-full right-0 mb-2";
      case "top-left":
        return "bottom-full left-0 mb-2";
      default:
        return "top-full left-0 mt-2";
    }
  };

  return (
    <div className="relative">
      <div ref={triggerRef} onClick={toggleMenu}>
        {trigger}
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className={`absolute ${getPositionClasses()} w-64 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20`}
            ref={menuRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <div
              className="py-2"
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="context-menu"
            >
              {title && (
                <div className="px-4 py-1 flex flex-col gap-1">
                  <span className="block font-bold text-sm text-black">
                    {title}
                  </span>
                </div>
              )}
              {options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleOptionClick(option)}
                  className="block px-4 py-1 text-sm text-blue hover:underline w-full text-left"
                  role="menuitem"
                >
                  {option.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContextMenu;
